import { respondCounterOffer } from "../features/buyer/buyeService";
import { dispute } from "../features/user/userService";

/**
 * An object containing API endpoint paths for authentication and product-related operations.
 * Use these endpoints to make HTTP requests to the backend server for actions such as user authentication,
 * registration, profile retrieval, and product listing or details fetching.
 *
 * @namespace endpoints
 * @property {Object} auth - Endpoints related to authentication.
 * @property {string} auth.login - Endpoint for user login.
 * @property {string} auth.register - Endpoint for user registration.
 * @property {string} auth.profile - Endpoint to retrieve the authenticated user's profile.
 * @property {Object} products - Endpoints related to products.
 * @property {string} products.list - Endpoint to fetch the list of products.
 * @property {function} products.details - Function that returns the endpoint for fetching details of a specific product by ID.
 * @param {string|number} id - The unique identifier of the product.
 * @returns {string} The endpoint URL for the product details.
 */
export const endpoints = {
  auth: {
    login: "/login",
    register: "/register",
    requestOtp: "/request-login-otp",
    verifyOtp: "/verify-otp",
    verifyLoginOtp: "/verify-login-otp",
    resendOtp: "resend-otp",
    resendVerification: "/resend-verification",
    forotPassword: "/forgot-password",
    resetPassword: "/reset-password",
    logout: "/logout",
  },

  user: {
    profile: {
      view: "/user/profile",
      update: "/user/profile/update",
      addresses: "/user/profile/addresses",
      updateAddress: "/user/profile/address",
      tradePrefrenceOptions: "/user/trade-preference-options",
      tradePreferenceStore: "/user/trade-preferences",
      mytradePreference: "/user/trade-preferences",
    },
    changePassword: "/change-password",
    kyc: {
      upload: "/kyc/upload",
      list: "/kyc/list",
    },
    notifications: {
      list: "/notifications",
      markRead: (id) => `/notifications/read/${id}`,
      clear: "/notifications/clear",
    },
    blogs: {
      blogsCategories: "/blog-categories",
      list: "/blogs",
      blogsByCategory: (id) => `/blogs/category/${id}`,
      blogDetailBySlug: (slug) => `/blogs/${slug}`,
      commonPages: "/pages",
      // pageContentbyGroup:(group) => `/pages/group/${group}`,
      pageDetailBySlug: (slug) => `/pages/slug/${slug}`,
    },
    dynamic: {
      siteMapUrls: "/sitemap-url",
      footerLinks: "/footer-links",
    },
    market: {
      priceExchangeRate: (currency, unit) => {
        if (currency && unit) {
          return `/exchange/metals/latest/${currency}/${unit}`;
        } else if (currency) {
          return `/exchange/metals/latest/${currency}`;
        } else {
          return `/exchange/metals/latest`;
        }
      },
      charts: "/charts",
      news: "/market/news",
    },
    chat: {
      chatCreation: "/chat/create",
      sendMessage: "/chat/send",
      chatMessages: (id) => `/chat/${id}/messages`,
      chatThreads: "/chat/threads",
      markAsRead: (id) => `/chat/${id}/mark-read`,
      typing: (id) => `/chat/${id}/typing`,
      unreadCount: "/chat/unread-count",
      dispute: (id) => `/chat/${id}/dispute`,
    },
    chatSupport: {
      chatSupportCreation: "/support/chat/create",
      sendSupportMessage: "/support/chat/send",
      chatSupportThreads: "/support/chat/threads",
      chatSupportMessages: (id) => `/support/chat/${id}/messages`,
      markSupportAsRead: (id) => `/support/chat/${id}/read`,
      closeSupportChat: (id) => `/support/chat/${id}/close`,
    },
    userRole: {
      switchRole: "/switch-role",
      verifyRoleOtp: "/role/switch/verify",
    },
  },

  buyer: {
    products: {
      list: "/buyer/products",
      detail: "/buyer/products/detail",
      filter: "/buyer/filter-products", // Use with query params ?category_id=1&subcategory_id=2
      subcategories: "/buyer/get-subcategories", // Use with ?category_id=1
      randomProducts: "/home-random-products",
    },
    bids: {
      list: "/buyer/bids",
      placeBid: "/buyer/bids/place",
      acceptBid: (id) => `/buyer/bids/accept/${id}`,
      rejectBid: (id) => `/buyer/bids/reject/${id}`,
    },
    dashboard: "/buyer/dashboard",
    orders: {
      list: "/buyer/orders",
      details: (id) => `/buyer/orders/${id}`,
      orderCancel: (id) => `/buyer/orders/${id}/cancel`,
      orderTrack: (id) => `/buyer/orders/track/${id}`,
      orderPlace: "/buyer/orders/place",
      orderSummary: (id) => `/buyer/order-summary/${id}`,
      invoice: "/buyer/order/invoice",
      markComplete: (id) => `/buyer/orders/${id}/confirm`,
      requestTpi: (id) => `/buyer/orders/${id}/request-tpi`
    },
    payments: {
      methods: "/buyer/payment-methods",
      createCheckoutSession: "/buyer/create-checkout-session",
      paymentStatus: (id) => `/buyer/payment-status/${id}`,
    },
    market: {
      data: "/market/data",
    },
    reverseBids: {
      list: "/buyer/my-requirements",
      store: "/buyer/requirements",
      details: (id) => `/buyer/requirements/${id}/bids`,
      accept: (id) => `/buyer/bids/${id}/accept`,
    },
    orderShipping: {
      orderShippingDetails: (id) => `/buyer/orders/${id}/shipping`,
      shippingDetails: (id) => `/buyer/orders/${id}/shipping`,
      acceptOrder: (id) => `/buyer/order/${id}/accept`,
      disputeOrder: (id) => `/buyer/order/${id}/dispute`,
    },
    contactUs: {
      request: "/contacts",
    },
    offers: {
      list: "/buyer/buyer-products",
      details: (id) => `/buyer/buyer-products/detail/${id}`,
      add: "/buyer/buyer-products",
      update: (id) => `/buyer/buyer-products/${id}`,
      delete: (id) => `/buyer/buyer-products/${id}`,
      calculatePrice: "/buyer/offer/calculate/price",
      specifications: (id) => `/buyer/subcategory/${id}/specifications`,
      metalCode: "/buyer/get-metal-code",
      counterOffer: (id) => `/buyer/counter-offers/${id}/respond`,
      pendingTransactions: "/buyer/transactions/pending",
      transactionDetails: (id) => `/buyer/transactions/${id}`,
      categories: "/buyer/categories",
      subcategories: (id) => `/buyer/subcategories?category_id=${id}`,
    },

    ai: {
      generateDescription: "/products/generate-description",
      generateFaqs: "/products/generate-faqs",
      editImage: "/ai/edit-image",
    },

    addressSelector: {
      countries: "/countries",
      states: "/states",
      cities: "/cities",
    },

    outOfRange: {
      list:"/buyer/out-of-range/requests",
      adminAssistance:(id) =>  `/buyer/out-of-range/request/${id}`,
      acceptCharges:(id) =>  `/buyer/out-of-range/accept/${id}`,
      rejectCharges:(id) =>  `/buyer/out-of-range/reject/${id}`,
    }
  },

  seller: {
    products: {
      list: "/seller/products",
      details: (id) => `/seller/products/${id}`,
      add: "/seller/products",
      update: (id) => `/seller/products/${id}`,
      delete: (id) => `/seller/products/${id}`,
      trash: "/seller/products/trash/list",
      restore: (id) => `/seller/products/${id}/restore`,
      calculatePrice: "/seller/products/calculate/price",
      dashboard: "/seller/dashboard",
      stockList: "/seller/products/stock/list",
      updateStock: (id) => `/seller/stock/${id}/update`,
      specifications: (id) => `/seller/subcategory/${id}/specifications`,
      metalPrice: "/metal-prices/trends",
      metalCode: "/seller/get-metal-code",
      generatVideoUrl: (id) => `/seller/products/${id}/generate-upload-link`,
    },
    bids: {
      list: "/seller/bids",
      statusUpdate: (id) => `/seller/bids/${id}/status`,
      
    },
    orders: {
      list: "/seller/orders",
      details: (id) => `/seller/orders/${id}`,
      orderStatusUpdate: (id) => `/seller/orders/${id}/status`,
      shippingPartnerList: "/seller/shipping-partners",
      shippinhPartnerUpdate: (id) => `/seller/orders/${id}/shipping-partner`,
      updateTrackingNumber: "/seller/orders/update-tracking-number",
      invoiceList: "/seller/invoices",
      invoiceDetails: (id) => `/seller/order/invoice/${id}`,
    },
    categories: {
      list: "/seller/categories",
    },
    subcategories: {
      list: (id) => `/seller/subcategories?category_id=${id}`,
    },
    reverseBids: {
      list: "/seller/requirements",
      orders: "/seller/reverse-bid-orders",
      store: (id) => `/seller/requirements/${id}/bids`,
    },
    certificates: {
      upload: "/seller/seller-certificates",  
      list: "/seller/seller-certificates",
    },
    payments: {
      methods: "/seller/stripe/onboarding",
      accountStatus: "/seller/stripe/status",
    },
    offers: {
      receivedOffers: "/seller/received-offers",
      respondCounterOffer: (id) => `/seller/offers/${id}/respond`,
      detail:(id)=> `/seller/offers/${id}/detail`
    },
    outOfRange: {
      list:"/seller/out-of-range-requests",
      approveRequest:(id) =>  `/seller/out-of-range/approve/${id}`,
      rejectRequest:(id) =>  `/seller/out-of-range/reject/${id}`,
    },
    ai: {
      generateDescription: "/products/generate-description",
      generateFaqs: "/products/generate-faqs",
      editImage: "/ai/edit-image",
    },
    onboarding:{
      list:"/seller/onboarding",
      submit:"/seller/onboarding/submits"
    }
  },
};
